import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit3, 
  Trash2, 
  Save, 
  X, 
  Loader2,
  TrendingUp,
  Target,
  Eye,
  Camera,
  Clock,
  Calendar,
  AlertCircle,
  CheckCircle,
  Play,
  RotateCcw,
  Infinity,
  Leaf,
  Droplets,
  Zap,
  TreePine,
  Recycle
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { useMissionImpacts } from '../../hooks/useMissionImpacts';
import { useImpactTypes } from '../../hooks/useImpactTypes';

interface Mission {
  id: string;
  title: string;
  description: string;
  category: string;
  difficulty: 'easy' | 'medium' | 'hard';
  points: number;
  time_frame: 'daily' | 'weekly' | 'monthly';
  verification_method: 'photo' | 'location' | 'quiz' | 'self-report';
  icon: string;
  trending: boolean;
  active: boolean;
  created_at: string;
  ar_enabled?: boolean;
  target_objects?: string[];
  target_count?: number;
  coming_soon?: boolean;
  launch_date?: string;
  // New permanent mission fields
  is_permanent?: boolean;
  max_completions_per_day?: number;
  cooldown_hours?: number;
}

interface MissionImpact {
  id: string;
  mission_id: string;
  impact_type_name: string;
  quantity_per_completion: number;
  unit: string;
  description: string;
}

// Comprehensive mock missions that match the database
const mockMissions: Mission[] = [
  {
    id: 'mission-1',
    title: 'Bring Your Own Bag',
    description: 'Use a reusable bag for shopping instead of plastic bags',
    category: 'Shopping',
    difficulty: 'easy',
    points: 50,
    time_frame: 'daily',
    verification_method: 'photo',
    icon: 'ShoppingBag',
    trending: true,
    active: true,
    created_at: '2024-12-08T00:00:00Z',
    ar_enabled: true,
    target_objects: ['bag', 'shopping_bag', 'reusable_bag'],
    target_count: 1,
    coming_soon: false,
    is_permanent: true,
    max_completions_per_day: 5,
    cooldown_hours: 1
  },
  {
    id: 'mission-2',
    title: '5-Minute Power Shower',
    description: 'Take a shower in 5 minutes or less to save water',
    category: 'Water',
    difficulty: 'medium',
    points: 75,
    time_frame: 'daily',
    verification_method: 'self-report',
    icon: 'Droplets',
    trending: false,
    active: true,
    created_at: '2024-12-08T00:00:00Z',
    ar_enabled: false,
    coming_soon: false,
    is_permanent: false,
    max_completions_per_day: 1,
    cooldown_hours: 0
  },
  {
    id: 'mission-3',
    title: 'Car-Free Commute',
    description: 'Walk, bike, or use public transport instead of driving',
    category: 'Transport',
    difficulty: 'medium',
    points: 100,
    time_frame: 'daily',
    verification_method: 'location',
    icon: 'Bike',
    trending: true,
    active: true,
    created_at: '2024-12-08T00:00:00Z',
    ar_enabled: false,
    coming_soon: false,
    is_permanent: false,
    max_completions_per_day: 1,
    cooldown_hours: 0
  },
  {
    id: 'mission-4',
    title: 'Plant-Based Meal',
    description: 'Share a photo of your delicious meat-free meal',
    category: 'Food',
    difficulty: 'easy',
    points: 60,
    time_frame: 'daily',
    verification_method: 'photo',
    icon: 'Leaf',
    trending: false,
    active: true,
    created_at: '2024-12-08T00:00:00Z',
    ar_enabled: true,
    target_objects: ['salad', 'vegetables', 'fruit', 'plant_food'],
    target_count: 1,
    coming_soon: false,
    is_permanent: true,
    max_completions_per_day: 3,
    cooldown_hours: 2
  },
  {
    id: 'mission-5',
    title: 'Community Clean-Up',
    description: 'Organize or join a local clean-up event with 5+ people',
    category: 'Community',
    difficulty: 'hard',
    points: 300,
    time_frame: 'monthly',
    verification_method: 'photo',
    icon: 'Users',
    trending: false,
    active: true,
    created_at: '2024-12-08T00:00:00Z',
    ar_enabled: false,
    coming_soon: false,
    is_permanent: false,
    max_completions_per_day: 1,
    cooldown_hours: 0
  },
  {
    id: 'mission-6',
    title: 'Plastic Bottle Collection',
    description: 'Collect and recycle plastic bottles - complete anytime you find bottles!',
    category: 'Waste',
    difficulty: 'medium',
    points: 150,
    time_frame: 'weekly',
    verification_method: 'photo',
    icon: 'Recycle',
    trending: false,
    active: true,
    created_at: '2024-12-08T00:00:00Z',
    ar_enabled: true,
    target_objects: ['bottle', 'plastic_bottle', 'water_bottle'],
    target_count: 3,
    coming_soon: false,
    is_permanent: true,
    max_completions_per_day: 5,
    cooldown_hours: 1
  }
];

export default function AdminMissions() {
  const [missions, setMissions] = useState<Mission[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingMission, setEditingMission] = useState<Mission | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showImpactModal, setShowImpactModal] = useState(false);
  const [selectedMissionForImpact, setSelectedMissionForImpact] = useState<Mission | null>(null);

  const { missionImpacts, getMissionImpacts, addMissionImpact } = useMissionImpacts();
  const { impactTypes } = useImpactTypes();

  const emptyMission: Omit<Mission, 'id' | 'created_at'> = {
    title: '',
    description: '',
    category: 'Energy',
    difficulty: 'easy',
    points: 50,
    time_frame: 'daily',
    verification_method: 'photo',
    icon: 'Target',
    trending: false,
    active: true,
    ar_enabled: false,
    target_objects: [],
    target_count: 1,
    coming_soon: false,
    launch_date: undefined,
    is_permanent: false,
    max_completions_per_day: 1,
    cooldown_hours: 0
  };

  useEffect(() => {
    fetchMissions();
  }, []);

  const fetchMissions = async () => {
    try {
      setLoading(true);
      
      if (!isSupabaseConfigured) {
        console.log('Using mock missions data for admin');
        setMissions(mockMissions);
        setLoading(false);
        return;
      }

      console.log('Fetching missions from database for admin...');
      
      const { data, error } = await supabase
        .from('missions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching missions:', error);
        setMissions(mockMissions);
        return;
      }

      console.log(`Admin: Found ${data?.length || 0} missions in database`);
      setMissions(data || []);
    } catch (error) {
      console.error('Error fetching missions:', error);
      setMissions(mockMissions);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (mission: Mission | Omit<Mission, 'id' | 'created_at'>) => {
    try {
      setSaving(true);

      if (!isSupabaseConfigured) {
        if ('id' in mission) {
          setMissions(prev => prev.map(m => m.id === mission.id ? mission : m));
        } else {
          const newMission = {
            ...mission,
            id: `mission-${Date.now()}`,
            created_at: new Date().toISOString()
          };
          setMissions(prev => [newMission, ...prev]);
        }
        setEditingMission(null);
        setIsCreating(false);
        setSaving(false);
        return;
      }

      if ('id' in mission) {
        const { error } = await supabase
          .from('missions')
          .update(mission)
          .eq('id', mission.id);

        if (error) {
          console.error('Error updating mission:', error);
          return;
        }
      } else {
        const { error } = await supabase
          .from('missions')
          .insert(mission);

        if (error) {
          console.error('Error creating mission:', error);
          return;
        }
      }

      await fetchMissions();
      setEditingMission(null);
      setIsCreating(false);
    } catch (error) {
      console.error('Error saving mission:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (missionId: string) => {
    if (!confirm('Are you sure you want to delete this mission?')) return;

    try {
      if (!isSupabaseConfigured) {
        setMissions(prev => prev.filter(m => m.id !== missionId));
        return;
      }

      const { error } = await supabase
        .from('missions')
        .delete()
        .eq('id', missionId);

      if (error) {
        console.error('Error deleting mission:', error);
        return;
      }

      await fetchMissions();
    } catch (error) {
      console.error('Error deleting mission:', error);
    }
  };

  const handleLaunchNow = async (missionId: string) => {
    if (!confirm('Are you sure you want to launch this mission now?')) return;

    try {
      const updateData = {
        coming_soon: false,
        active: true,
        launch_date: null
      };

      if (!isSupabaseConfigured) {
        setMissions(prev => prev.map(m => 
          m.id === missionId ? { ...m, ...updateData } : m
        ));
        return;
      }

      const { error } = await supabase
        .from('missions')
        .update(updateData)
        .eq('id', missionId);

      if (error) {
        console.error('Error launching mission:', error);
        return;
      }

      await fetchMissions();
    } catch (error) {
      console.error('Error launching mission:', error);
    }
  };

  const handleConfigureImpacts = (mission: Mission) => {
    setSelectedMissionForImpact(mission);
    setShowImpactModal(true);
  };

  const handleAddDefaultImpacts = async (mission: Mission) => {
    if (!confirm(`Add default impact mappings for "${mission.title}"?`)) return;
    
    try {
      // Determine appropriate impact types based on mission category and title
      const impactsToAdd: Omit<MissionImpact, 'id'>[] = [];
      
      // Add waste reduction for waste-related missions
      if (mission.category === 'Waste' || mission.title.includes('Bottle') || mission.title.includes('Bag')) {
        impactsToAdd.push({
          mission_id: mission.id,
          impact_type_name: 'waste_reduced',
          quantity_per_completion: mission.title.includes('Bottle') ? 0.5 : 0.1,
          unit: 'kg',
          description: mission.title.includes('Bottle') 
            ? 'Plastic waste diverted from landfills' 
            : 'Plastic bag waste prevented'
        });
      }
      
      // Add CO2 savings for transport or energy missions
      if (mission.category === 'Transport' || mission.category === 'Energy' || mission.title.includes('Commute')) {
        impactsToAdd.push({
          mission_id: mission.id,
          impact_type_name: 'co2_saved',
          quantity_per_completion: mission.title.includes('Commute') ? 2.5 : 1.0,
          unit: 'kg',
          description: mission.title.includes('Commute')
            ? 'CO2 emissions avoided by not driving'
            : 'CO2 emissions reduced'
        });
      }
      
      // Add water savings for water-related missions
      if (mission.category === 'Water' || mission.title.includes('Shower')) {
        impactsToAdd.push({
          mission_id: mission.id,
          impact_type_name: 'water_saved',
          quantity_per_completion: mission.title.includes('Shower') ? 50 : 10,
          unit: 'L',
          description: mission.title.includes('Shower')
            ? 'Water saved by shorter shower duration'
            : 'Water conservation'
        });
      }
      
      // Add energy savings for energy-related missions
      if (mission.category === 'Energy' || mission.title.includes('Shower')) {
        impactsToAdd.push({
          mission_id: mission.id,
          impact_type_name: 'energy_saved',
          quantity_per_completion: mission.title.includes('Shower') ? 2.0 : 1.0,
          unit: 'kWh',
          description: mission.title.includes('Shower')
            ? 'Energy saved from reduced hot water heating'
            : 'Energy conservation'
        });
      }
      
      // Add tree planting equivalent for community missions
      if (mission.category === 'Community' || mission.title.includes('Clean-Up')) {
        impactsToAdd.push({
          mission_id: mission.id,
          impact_type_name: 'trees_planted',
          quantity_per_completion: 5,
          unit: '',
          description: 'Environmental restoration equivalent through community action'
        });
      }
      
      // Add CO2 savings for food-related missions
      if (mission.category === 'Food' || mission.title.includes('Plant-Based')) {
        impactsToAdd.push({
          mission_id: mission.id,
          impact_type_name: 'co2_saved',
          quantity_per_completion: 1.8,
          unit: 'kg',
          description: 'CO2 emissions saved vs meat-based meal'
        });
        
        // Also add water savings for plant-based meals
        impactsToAdd.push({
          mission_id: mission.id,
          impact_type_name: 'water_saved',
          quantity_per_completion: 20,
          unit: 'L',
          description: 'Water saved vs meat production'
        });
      }
      
      // If no specific impacts were determined, add a generic CO2 impact
      if (impactsToAdd.length === 0) {
        impactsToAdd.push({
          mission_id: mission.id,
          impact_type_name: 'co2_saved',
          quantity_per_completion: 1.0,
          unit: 'kg',
          description: 'General CO2 reduction from eco-friendly action'
        });
      }
      
      // Add all determined impacts
      for (const impact of impactsToAdd) {
        await addMissionImpact(impact);
      }
      
      alert(`Added ${impactsToAdd.length} impact mappings to "${mission.title}"`);
    } catch (error) {
      console.error('Error adding default impacts:', error);
      alert('Failed to add default impacts');
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'hard': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTimeFrameColor = (timeFrame: string) => {
    switch (timeFrame) {
      case 'daily': return 'bg-blue-100 text-blue-800';
      case 'weekly': return 'bg-purple-100 text-purple-800';
      case 'monthly': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (mission: Mission) => {
    if (mission.coming_soon) return 'bg-purple-100 text-purple-800';
    if (mission.active) return 'bg-green-100 text-green-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (mission: Mission) => {
    if (mission.coming_soon) return 'Coming Soon';
    if (mission.active) return 'Active';
    return 'Inactive';
  };

  const formatTimeUntilLaunch = (launchDate: string) => {
    const now = new Date().getTime();
    const launch = new Date(launchDate).getTime();
    const diff = launch - now;

    if (diff <= 0) return 'Ready to launch';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  const activeMissions = missions.filter(m => m.active && !m.coming_soon);
  const comingSoonMissions = missions.filter(m => m.coming_soon);
  const inactiveMissions = missions.filter(m => !m.active && !m.coming_soon);
  const permanentMissions = missions.filter(m => m.is_permanent);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Mission Management</h2>
          <p className="text-gray-600 mt-2">Create and manage eco missions with permanent and one-time options</p>
        </div>
        <button
          onClick={() => setIsCreating(true)}
          className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2"
        >
          <Plus className="h-5 w-5" />
          <span>Add Mission</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="bg-emerald-100 p-2 rounded-lg">
              <Target className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{missions.length}</p>
              <p className="text-sm text-gray-600">Total Missions</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="bg-green-100 p-2 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{activeMissions.length}</p>
              <p className="text-sm text-gray-600">Active</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <RotateCcw className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{permanentMissions.length}</p>
              <p className="text-sm text-gray-600">Permanent</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="bg-purple-100 p-2 rounded-lg">
              <Clock className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{comingSoonMissions.length}</p>
              <p className="text-sm text-gray-600">Coming Soon</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <TrendingUp className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {missions.filter(m => m.trending).length}
              </p>
              <p className="text-sm text-gray-600">Trending</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="bg-purple-100 p-2 rounded-lg">
              <Eye className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {missions.filter(m => m.ar_enabled).length}
              </p>
              <p className="text-sm text-gray-600">AR Enabled</p>
            </div>
          </div>
        </div>
      </div>

      {/* Mission Form Modal */}
      {(editingMission || isCreating) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                {editingMission ? 'Edit Mission' : 'Create New Mission'}
              </h3>
              <button
                onClick={() => {
                  setEditingMission(null);
                  setIsCreating(false);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <MissionForm
              mission={editingMission || emptyMission}
              onSave={handleSave}
              saving={saving}
            />
          </div>
        </div>
      )}

      {/* Impact Configuration Modal */}
      {showImpactModal && selectedMissionForImpact && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                Configure Environmental Impacts: {selectedMissionForImpact.title}
              </h3>
              <button
                onClick={() => {
                  setSelectedMissionForImpact(null);
                  setShowImpactModal(false);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <MissionImpactForm 
              mission={selectedMissionForImpact}
              impactTypes={impactTypes}
              missionImpacts={getMissionImpacts(selectedMissionForImpact.id)}
              onAddImpact={addMissionImpact}
              onClose={() => {
                setSelectedMissionForImpact(null);
                setShowImpactModal(false);
              }}
            />
          </div>
        </div>
      )}

      {/* Coming Soon Missions Section */}
      {comingSoonMissions.length > 0 && (
        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
            <Clock className="h-6 w-6 text-purple-600" />
            <span>Coming Soon Missions</span>
          </h3>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {comingSoonMissions.map((mission) => (
              <ComingSoonMissionCard 
                key={mission.id} 
                mission={mission} 
                onEdit={setEditingMission}
                onDelete={handleDelete}
                onLaunchNow={handleLaunchNow}
                onConfigureImpacts={handleConfigureImpacts}
                formatTimeUntilLaunch={formatTimeUntilLaunch}
                missionImpacts={getMissionImpacts(mission.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Active Missions Section */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
          <CheckCircle className="h-6 w-6 text-green-600" />
          <span>Active Missions</span>
        </h3>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {activeMissions.map((mission) => (
            <MissionCard 
              key={mission.id} 
              mission={mission} 
              onEdit={setEditingMission}
              onDelete={handleDelete}
              onConfigureImpacts={handleConfigureImpacts}
              getDifficultyColor={getDifficultyColor}
              getTimeFrameColor={getTimeFrameColor}
              getStatusColor={getStatusColor}
              getStatusLabel={getStatusLabel}
              missionImpacts={getMissionImpacts(mission.id)}
            />
          ))}
        </div>
      </div>

      {/* Inactive Missions Section */}
      {inactiveMissions.length > 0 && (
        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
            <AlertCircle className="h-6 w-6 text-gray-600" />
            <span>Inactive Missions</span>
          </h3>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {inactiveMissions.map((mission) => (
              <MissionCard 
                key={mission.id} 
                mission={mission} 
                onEdit={setEditingMission}
                onDelete={handleDelete}
                onConfigureImpacts={handleConfigureImpacts}
                getDifficultyColor={getDifficultyColor}
                getTimeFrameColor={getTimeFrameColor}
                getStatusColor={getStatusColor}
                getStatusLabel={getStatusLabel}
                missionImpacts={getMissionImpacts(mission.id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Coming Soon Mission Card Component
function ComingSoonMissionCard({ 
  mission, 
  onEdit, 
  onDelete, 
  onLaunchNow,
  onConfigureImpacts,
  formatTimeUntilLaunch,
  missionImpacts
}: { 
  mission: Mission;
  onEdit: (mission: Mission) => void;
  onDelete: (id: string) => void;
  onLaunchNow: (id: string) => void;
  onConfigureImpacts: (mission: Mission) => void;
  formatTimeUntilLaunch: (date: string) => string;
  missionImpacts: MissionImpact[];
}) {
  const IconComponent = (LucideIcons as any)[mission.icon] || Target;
  const isReadyToLaunch = mission.launch_date && new Date(mission.launch_date).getTime() <= Date.now();

  return (
    <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-6 relative overflow-hidden">
      <div className="absolute top-4 right-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full text-xs font-bold">
        COMING SOON
      </div>

      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start space-x-3">
          <div className="bg-purple-100 p-2 rounded-lg">
            <IconComponent className="h-5 w-5 text-purple-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1 flex-wrap">
              <h4 className="font-semibold text-gray-900">{mission.title}</h4>
              {mission.trending && (
                <TrendingUp className="h-4 w-4 text-orange-500" />
              )}
              {mission.is_permanent && (
                <div className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium flex items-center space-x-1">
                  <RotateCcw className="h-3 w-3" />
                  <span>Permanent</span>
                </div>
              )}
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">{mission.description}</p>
          </div>
        </div>
      </div>

      {mission.launch_date && (
        <div className="mb-4 bg-gradient-to-r from-purple-100 to-pink-100 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <Calendar className="h-5 w-5 text-purple-600" />
            <span className="text-sm font-medium text-purple-700">
              {isReadyToLaunch ? 'Ready to Launch!' : 'Launches in:'}
            </span>
          </div>
          <div className="text-center">
            <p className={`text-2xl font-bold font-mono ${isReadyToLaunch ? 'text-green-600' : 'text-purple-600'}`}>
              {isReadyToLaunch ? 'NOW' : formatTimeUntilLaunch(mission.launch_date)}
            </p>
            {!isReadyToLaunch && (
              <p className="text-xs text-purple-600 mt-1">
                {new Date(mission.launch_date).toLocaleDateString()} at {new Date(mission.launch_date).toLocaleTimeString()}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
          {mission.difficulty}
        </span>
        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
          {mission.time_frame}
        </span>
        <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs font-medium">
          {mission.category}
        </span>
        {mission.is_permanent && (
          <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium">
            Max: {mission.max_completions_per_day}/day
          </span>
        )}
      </div>

      {/* Impact Badges */}
      {missionImpacts.length > 0 && (
        <div className="mb-4">
          <div className="flex flex-wrap gap-1">
            {missionImpacts.map((impact, index) => {
              const impactIcon = getImpactIcon(impact.impact_type_name);
              return (
                <span key={index} className="bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1">
                  {impactIcon}
                  <span>+{impact.quantity_per_completion} {impact.unit}</span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
          <span className="text-lg font-bold text-gray-900">{mission.points} EcoCoins</span>
        </div>
        
        <div className="flex items-center space-x-2">
          {isReadyToLaunch && (
            <button
              onClick={() => onLaunchNow(mission.id)}
              className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded-lg text-sm font-medium transition-colors flex items-center space-x-1"
            >
              <Play className="h-3 w-3" />
              <span>Launch Now</span>
            </button>
          )}
          <button
            onClick={() => onConfigureImpacts(mission)}
            className="p-2 text-gray-600 hover:text-emerald-600 rounded-lg transition-colors"
            title="Configure Environmental Impacts"
          >
            <Leaf className="h-4 w-4" />
          </button>
          <button
            onClick={() => onEdit(mission)}
            className="p-2 text-gray-600 hover:text-purple-600 rounded-lg transition-colors"
          >
            <Edit3 className="h-4 w-4" />
          </button>
          <button
            onClick={() => onDelete(mission.id)}
            className="p-2 text-gray-600 hover:text-red-600 rounded-lg transition-colors"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Regular Mission Card Component
function MissionCard({ 
  mission, 
  onEdit, 
  onDelete,
  onConfigureImpacts,
  getDifficultyColor,
  getTimeFrameColor,
  getStatusColor,
  getStatusLabel,
  missionImpacts
}: { 
  mission: Mission;
  onEdit: (mission: Mission) => void;
  onDelete: (id: string) => void;
  onConfigureImpacts: (mission: Mission) => void;
  getDifficultyColor: (difficulty: string) => string;
  getTimeFrameColor: (timeFrame: string) => string;
  getStatusColor: (mission: Mission) => string;
  getStatusLabel: (mission: Mission) => string;
  missionImpacts: MissionImpact[];
}) {
  const IconComponent = (LucideIcons as any)[mission.icon] || Target;

  return (
    <div className={`bg-white border rounded-xl p-6 hover:shadow-md transition-shadow ${
      mission.is_permanent ? 'border-l-4 border-l-blue-500' : 'border-gray-200'
    }`}>
      {/* Permanent Mission Badge */}
      {mission.is_permanent && (
        <div className="absolute top-4 right-4 bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-2 py-1 rounded-full text-xs font-bold">
          REPEATABLE
        </div>
      )}

      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start space-x-3">
          <div className={`p-2 rounded-lg ${mission.is_permanent ? 'bg-blue-100' : 'bg-emerald-100'}`}>
            <IconComponent className={`h-5 w-5 ${mission.is_permanent ? 'text-blue-600' : 'text-emerald-600'}`} />
          </div>
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1 flex-wrap">
              <h4 className="font-semibold text-gray-900">{mission.title}</h4>
              {mission.trending && (
                <TrendingUp className="h-4 w-4 text-orange-500" />
              )}
              {mission.ar_enabled && (
                <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-2 py-0.5 rounded-full text-xs font-medium flex items-center space-x-1">
                  <Eye className="h-3 w-3" />
                  <span>AR</span>
                </div>
              )}
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">{mission.description}</p>
          </div>
        </div>
      </div>

      {/* Permanent Mission Settings */}
      {mission.is_permanent && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-2">
            <RotateCcw className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-700">Permanent Mission Settings</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs text-blue-600">
            <div><strong>Max per day:</strong> {mission.max_completions_per_day || 1}</div>
            <div><strong>Cooldown:</strong> {mission.cooldown_hours || 0}h</div>
          </div>
        </div>
      )}

      {/* AR Detection Info */}
      {mission.ar_enabled && mission.target_objects && mission.target_objects.length > 0 && (
        <div className="mb-4 bg-purple-50 border border-purple-200 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-2">
            <Eye className="h-4 w-4 text-purple-600" />
            <span className="text-sm font-medium text-purple-700">AR Detection</span>
          </div>
          <div className="text-xs text-purple-600">
            <div><strong>Objects:</strong> {mission.target_objects.join(', ')}</div>
            <div><strong>Count:</strong> {mission.target_count || 1}</div>
          </div>
        </div>
      )}

      {/* Impact Badges */}
      {missionImpacts.length > 0 && (
        <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-2">
            <Leaf className="h-4 w-4 text-emerald-600" />
            <span className="text-sm font-medium text-emerald-700">Environmental Impact</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {missionImpacts.map((impact, index) => {
              const impactIcon = getImpactIcon(impact.impact_type_name);
              return (
                <span key={index} className="bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1">
                  {impactIcon}
                  <span>+{impact.quantity_per_completion} {impact.unit}</span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(mission.difficulty)}`}>
          {mission.difficulty}
        </span>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTimeFrameColor(mission.time_frame)}`}>
          {mission.time_frame}
        </span>
        <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs font-medium">
          {mission.category}
        </span>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(mission)}`}>
          {getStatusLabel(mission)}
        </span>
        <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1">
          <Camera className="h-3 w-3" />
          <span>{mission.verification_method}</span>
        </span>
        {mission.is_permanent && (
          <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1">
            <Infinity className="h-3 w-3" />
            <span>Repeatable</span>
          </span>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
          <span className="text-lg font-bold text-gray-900">+{mission.points} pts</span>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => onConfigureImpacts(mission)}
            className="p-2 text-gray-600 hover:text-emerald-600 rounded-lg transition-colors"
            title="Configure Environmental Impacts"
          >
            <Leaf className="h-4 w-4" />
          </button>
          <button
            onClick={() => onEdit(mission)}
            className="p-2 text-gray-600 hover:text-emerald-600 rounded-lg transition-colors"
          >
            <Edit3 className="h-4 w-4" />
          </button>
          <button
            onClick={() => onDelete(mission.id)}
            className="p-2 text-gray-600 hover:text-red-600 rounded-lg transition-colors"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper function to get impact icon
function getImpactIcon(impactType: string) {
  switch (impactType) {
    case 'co2_saved':
      return <Leaf className="h-3 w-3 text-green-600" />;
    case 'waste_reduced':
      return <Recycle className="h-3 w-3 text-orange-600" />;
    case 'trees_planted':
      return <TreePine className="h-3 w-3 text-emerald-600" />;
    case 'water_saved':
      return <Droplets className="h-3 w-3 text-blue-600" />;
    case 'energy_saved':
      return <Zap className="h-3 w-3 text-yellow-600" />;
    default:
      return <Leaf className="h-3 w-3 text-emerald-600" />;
  }
}

// Mission Form Component
function MissionForm({ 
  mission, 
  onSave, 
  saving 
}: { 
  mission: Mission | Omit<Mission, 'id' | 'created_at'>;
  onSave: (mission: Mission | Omit<Mission, 'id' | 'created_at'>) => void;
  saving: boolean;
}) {
  const [formData, setFormData] = useState(mission);

  const categories = ['Energy', 'Transport', 'Waste', 'Water', 'Food', 'Community', 'Shopping'];
  const difficulties = ['easy', 'medium', 'hard'];
  const timeFrames = ['daily', 'weekly', 'monthly'];
  const verificationMethods = ['photo', 'location', 'quiz', 'self-report'];
  const iconOptions = ['Target', 'Leaf', 'Droplets', 'Bike', 'ShoppingBag', 'Users', 'Recycle', 'Zap', 'TreePine'];

  const commonTargetObjects = [
    'bottle', 'plastic_bottle', 'water_bottle', 'glass_bottle',
    'bag', 'shopping_bag', 'reusable_bag', 'paper_bag',
    'vegetables', 'fruit', 'salad', 'plant_food',
    'led_bulb', 'light_bulb', 'bulb',
    'compost', 'compost_bin', 'organic_waste',
    'bicycle', 'bike', 'public_transport',
    'solar_panel', 'renewable_energy',
    'recycling_bin', 'trash', 'waste'
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleTargetObjectsChange = (value: string) => {
    const objects = value.split(',').map(obj => obj.trim()).filter(obj => obj.length > 0);
    setFormData({ ...formData, target_objects: objects });
  };

  const formatDateTimeLocal = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleLaunchDateChange = (value: string) => {
    setFormData({ 
      ...formData, 
      launch_date: value ? new Date(value).toISOString() : undefined 
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          rows={3}
          required
        />
      </div>

      {/* Mission Settings */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Difficulty</label>
          <select
            value={formData.difficulty}
            onChange={(e) => setFormData({ ...formData, difficulty: e.target.value as any })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            {difficulties.map(diff => (
              <option key={diff} value={diff}>{diff}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Time Frame</label>
          <select
            value={formData.time_frame}
            onChange={(e) => setFormData({ ...formData, time_frame: e.target.value as any })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            {timeFrames.map(tf => (
              <option key={tf} value={tf}>{tf}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Points</label>
          <input
            type="number"
            value={formData.points}
            onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) || 0 })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            min="0"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Verification Method</label>
          <select
            value={formData.verification_method}
            onChange={(e) => setFormData({ ...formData, verification_method: e.target.value as any })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            {verificationMethods.map(vm => (
              <option key={vm} value={vm}>{vm}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
          <select
            value={formData.icon}
            onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            {iconOptions.map(icon => (
              <option key={icon} value={icon}>{icon}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Permanent Mission Settings */}
      <div className="border-t border-gray-200 pt-6">
        <div className="flex items-center space-x-2 mb-4">
          <RotateCcw className="h-5 w-5 text-blue-600" />
          <h4 className="text-lg font-medium text-gray-900">Permanent Mission Settings</h4>
        </div>

        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="is_permanent"
              checked={formData.is_permanent || false}
              onChange={(e) => setFormData({ 
                ...formData, 
                is_permanent: e.target.checked,
                max_completions_per_day: e.target.checked ? (formData.max_completions_per_day || 1) : 1,
                cooldown_hours: e.target.checked ? (formData.cooldown_hours || 0) : 0
              })}
              className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            <label htmlFor="is_permanent" className="text-sm text-gray-700">
              Make this a permanent mission (users can complete multiple times)
            </label>
          </div>

          {formData.is_permanent && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-blue-50 p-4 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Completions Per Day
                </label>
                <input
                  type="number"
                  value={formData.max_completions_per_day || 1}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    max_completions_per_day: parseInt(e.target.value) || 1 
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  min="1"
                  max="20"
                />
                <p className="text-xs text-gray-500 mt-1">
                  How many times per day can users complete this mission?
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cooldown Hours
                </label>
                <input
                  type="number"
                  value={formData.cooldown_hours || 0}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    cooldown_hours: parseInt(e.target.value) || 0 
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  min="0"
                  max="24"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Hours to wait between completions (0 = no cooldown)
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Launch Settings */}
      <div className="border-t border-gray-200 pt-6">
        <div className="flex items-center space-x-2 mb-4">
          <Clock className="h-5 w-5 text-purple-600" />
          <h4 className="text-lg font-medium text-gray-900">Launch Settings</h4>
        </div>

        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="coming_soon"
              checked={formData.coming_soon || false}
              onChange={(e) => setFormData({ 
                ...formData, 
                coming_soon: e.target.checked,
                active: e.target.checked ? false : formData.active
              })}
              className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            <label htmlFor="coming_soon" className="text-sm text-gray-700">
              Mark as "Coming Soon" (users will see countdown but cannot start)
            </label>
          </div>

          {formData.coming_soon && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Launch Date & Time (Optional)
              </label>
              <input
                type="datetime-local"
                value={formatDateTimeLocal(formData.launch_date)}
                onChange={(e) => handleLaunchDateChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave empty for "Coming Soon" without specific date
              </p>
            </div>
          )}
        </div>
      </div>

      {/* AR Detection Settings */}
      <div className="border-t border-gray-200 pt-6">
        <div className="flex items-center space-x-2 mb-4">
          <Eye className="h-5 w-5 text-purple-600" />
          <h4 className="text-lg font-medium text-gray-900">AR Detection Settings</h4>
        </div>

        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="ar_enabled"
              checked={formData.ar_enabled || false}
              onChange={(e) => setFormData({ ...formData, ar_enabled: e.target.checked })}
              className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            <label htmlFor="ar_enabled" className="text-sm text-gray-700">
              Enable AR detection for this mission
            </label>
          </div>

          {formData.ar_enabled && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Objects (comma-separated)
                </label>
                <input
                  type="text"
                  value={formData.target_objects?.join(', ') || ''}
                  onChange={(e) => handleTargetObjectsChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="e.g., bottle, plastic_bottle, water_bottle"
                />
                <div className="mt-2">
                  <p className="text-xs text-gray-500 mb-2">Common objects:</p>
                  <div className="flex flex-wrap gap-1">
                    {commonTargetObjects.map(obj => (
                      <button
                        key={obj}
                        type="button"
                        onClick={() => {
                          const currentObjects = formData.target_objects || [];
                          if (!currentObjects.includes(obj)) {
                            setFormData({ 
                              ...formData, 
                              target_objects: [...currentObjects, obj] 
                            });
                          }
                        }}
                        className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded transition-colors"
                      >
                        {obj}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Count
                </label>
                <input
                  type="number"
                  value={formData.target_count || 1}
                  onChange={(e) => setFormData({ ...formData, target_count: parseInt(e.target.value) || 1 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  min="1"
                  max="10"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Number of objects that need to be detected to complete the mission
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Status Settings */}
      <div className="flex items-center space-x-4">
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={formData.trending}
            onChange={(e) => setFormData({ ...formData, trending: e.target.checked })}
            className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
          />
          <span className="text-sm text-gray-700">Trending</span>
        </label>

        {!formData.coming_soon && (
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={formData.active}
              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            <span className="text-sm text-gray-700">Active</span>
          </label>
        )}
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="submit"
          disabled={saving}
          className="bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Saving...</span>
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              <span>Save Mission</span>
            </>
          )}
        </button>
      </div>
    </form>
  );
}

// Mission Impact Form Component
function MissionImpactForm({
  mission,
  impactTypes,
  missionImpacts,
  onAddImpact,
  onClose
}: {
  mission: Mission;
  impactTypes: any[];
  missionImpacts: MissionImpact[];
  onAddImpact: (impact: Omit<MissionImpact, 'id'>) => Promise<{ error: any }>;
  onClose: () => void;
}) {
  const [newImpact, setNewImpact] = useState<Omit<MissionImpact, 'id'>>({
    mission_id: mission.id,
    impact_type_name: '',
    quantity_per_completion: 1,
    unit: '',
    description: ''
  });
  const [saving, setSaving] = useState(false);

  const handleAddImpact = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await onAddImpact(newImpact);
      
      // Reset form
      setNewImpact({
        mission_id: mission.id,
        impact_type_name: '',
        quantity_per_completion: 1,
        unit: '',
        description: ''
      });
    } catch (error) {
      console.error('Error adding impact:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleAddDefaultImpacts = async () => {
    if (!confirm(`Add default impact mappings for "${mission.title}"?`)) return;
    
    setSaving(true);
    try {
      // Determine appropriate impact types based on mission category and title
      const impactsToAdd: Omit<MissionImpact, 'id'>[] = [];
      
      // Add waste reduction for waste-related missions
      if (mission.category === 'Waste' || mission.title.includes('Bottle') || mission.title.includes('Bag')) {
        impactsToAdd.push({
          mission_id: mission.id,
          impact_type_name: 'waste_reduced',
          quantity_per_completion: mission.title.includes('Bottle') ? 0.5 : 0.1,
          unit: 'kg',
          description: mission.title.includes('Bottle') 
            ? 'Plastic waste diverted from landfills' 
            : 'Plastic bag waste prevented'
        });
      }
      
      // Add CO2 savings for transport or energy missions
      if (mission.category === 'Transport' || mission.category === 'Energy' || mission.title.includes('Commute')) {
        impactsToAdd.push({
          mission_id: mission.id,
          impact_type_name: 'co2_saved',
          quantity_per_completion: mission.title.includes('Commute') ? 2.5 : 1.0,
          unit: 'kg',
          description: mission.title.includes('Commute')
            ? 'CO2 emissions avoided by not driving'
            : 'CO2 emissions reduced'
        });
      }
      
      // Add water savings for water-related missions
      if (mission.category === 'Water' || mission.title.includes('Shower')) {
        impactsToAdd.push({
          mission_id: mission.id,
          impact_type_name: 'water_saved',
          quantity_per_completion: mission.title.includes('Shower') ? 50 : 10,
          unit: 'L',
          description: mission.title.includes('Shower')
            ? 'Water saved by shorter shower duration'
            : 'Water conservation'
        });
      }
      
      // Add energy savings for energy-related missions
      if (mission.category === 'Energy' || mission.title.includes('Shower')) {
        impactsToAdd.push({
          mission_id: mission.id,
          impact_type_name: 'energy_saved',
          quantity_per_completion: mission.title.includes('Shower') ? 2.0 : 1.0,
          unit: 'kWh',
          description: mission.title.includes('Shower')
            ? 'Energy saved from reduced hot water heating'
            : 'Energy conservation'
        });
      }
      
      // Add tree planting equivalent for community missions
      if (mission.category === 'Community' || mission.title.includes('Clean-Up')) {
        impactsToAdd.push({
          mission_id: mission.id,
          impact_type_name: 'trees_planted',
          quantity_per_completion: 5,
          unit: '',
          description: 'Environmental restoration equivalent through community action'
        });
      }
      
      // Add CO2 savings for food-related missions
      if (mission.category === 'Food' || mission.title.includes('Plant-Based')) {
        impactsToAdd.push({
          mission_id: mission.id,
          impact_type_name: 'co2_saved',
          quantity_per_completion: 1.8,
          unit: 'kg',
          description: 'CO2 emissions saved vs meat-based meal'
        });
        
        // Also add water savings for plant-based meals
        impactsToAdd.push({
          mission_id: mission.id,
          impact_type_name: 'water_saved',
          quantity_per_completion: 20,
          unit: 'L',
          description: 'Water saved vs meat production'
        });
      }
      
      // If no specific impacts were determined, add a generic CO2 impact
      if (impactsToAdd.length === 0) {
        impactsToAdd.push({
          mission_id: mission.id,
          impact_type_name: 'co2_saved',
          quantity_per_completion: 1.0,
          unit: 'kg',
          description: 'General CO2 reduction from eco-friendly action'
        });
      }
      
      // Add all determined impacts
      for (const impact of impactsToAdd) {
        await onAddImpact(impact);
      }
      
      alert(`Added ${impactsToAdd.length} impact mappings to "${mission.title}"`);
    } catch (error) {
      console.error('Error adding default impacts:', error);
      alert('Failed to add default impacts');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Current Impacts */}
      <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-medium text-gray-900">Current Impact Mappings</h4>
          <button
            onClick={handleAddDefaultImpacts}
            disabled={saving}
            className="bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Zap className="h-4 w-4" />
            )}
            <span>Add Default Impacts</span>
          </button>
        </div>
        
        {missionImpacts.length > 0 ? (
          <div className="space-y-3">
            {missionImpacts.map((impact, index) => {
              const impactType = impactTypes.find(t => t.name === impact.impact_type_name);
              const IconComponent = impactType ? (LucideIcons as any)[impactType.icon] || Target : Leaf;
              
              return (
                <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${impactType?.bg_color || 'bg-gray-100'}`}>
                      <IconComponent className={`h-5 w-5 ${impactType?.color || 'text-gray-600'}`} />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-medium text-gray-900">
                        {impactType?.label || impact.impact_type_name}
                      </h5>
                      <p className="text-sm text-gray-600">{impact.description}</p>
                      <div className="mt-1 text-sm font-medium text-emerald-600">
                        +{impact.quantity_per_completion} {impact.unit} per completion
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-6">
            <p className="text-gray-500">No impact mappings configured yet</p>
          </div>
        )}
      </div>

      {/* Add New Impact */}
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <h4 className="font-medium text-gray-900 mb-4">Add New Impact</h4>
        
        <form onSubmit={handleAddImpact} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Impact Type</label>
              <select
                value={newImpact.impact_type_name}
                onChange={(e) => {
                  const selectedType = impactTypes.find(t => t.name === e.target.value);
                  setNewImpact({ 
                    ...newImpact, 
                    impact_type_name: e.target.value,
                    unit: selectedType?.unit || ''
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                required
              >
                <option value="">Select impact type...</option>
                {impactTypes.filter(t => t.active).map(type => (
                  <option key={type.id} value={type.name}>{type.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Quantity per Completion</label>
              <input
                type="number"
                step="0.1"
                value={newImpact.quantity_per_completion}
                onChange={(e) => setNewImpact({ ...newImpact, quantity_per_completion: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="e.g., 0.5"
                required
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Unit</label>
              <input
                type="text"
                value={newImpact.unit}
                onChange={(e) => setNewImpact({ ...newImpact, unit: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="e.g., kg, L, kWh"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <input
                type="text"
                value={newImpact.description}
                onChange={(e) => setNewImpact({ ...newImpact, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="Describe this impact..."
                required
              />
            </div>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              type="submit"
              disabled={saving}
              className="bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              <span>Add Impact</span>
            </button>
          </div>
        </form>
      </div>

      {/* Close Button */}
      <div className="flex justify-end">
        <button
          onClick={onClose}
          className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-2 rounded-lg font-medium transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );
}